import React, { useEffect, useMemo, useState } from 'react'
import { backendService } from '../../services/backendService'
import { logError } from '../../utils/errorLogger'
import './TemplateUploadDialog.css'

interface TemplateItem {
  id: string
  name: string
  size?: string
  type?: string
  is_shared?: boolean
  created_by?: string
  outline_points?: number[][]
  landmarks?: Record<string, number[]>
}

export interface TemplateOverlay {
  id: string
  name: string
  points: number[][] // 2D points (x, y) in mm — already adjusted by translate/rotate/scale sliders
  landmarks?: Record<string, number[]>
  visible: boolean
  color?: string
  /**
   * Optional centroid (mean of x, mean of y) of the ORIGINAL aligned points
   * before any user translate/rotate/scale was applied. Canvas3D uses this
   * as the centering anchor so user-applied translations are NOT cancelled
   * by re-centering on the moved points.
   */
  originCentroid?: [number, number]
  /** Optional Z offset (mm) applied to the rendered overlay group in 3D. */
  zOffset?: number
}

export interface TemplateVisualizerDialogProps {
  isOpen: boolean
  onClose: () => void
  overlay: TemplateOverlay | null
  onOverlayChange: (overlay: TemplateOverlay | null) => void
}

const SVG_W = 360
const SVG_H = 480
const PAD = 16

function buildPathFromPoints(pts: number[][]): {
  d: string
  bbox: { minX: number; minY: number; maxX: number; maxY: number; w: number; h: number }
  scale: number
  ox: number
  oy: number
} | null {
  if (!pts || pts.length < 3) return null
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const [x, y] of pts) {
    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  }
  const w = maxX - minX || 1
  const h = maxY - minY || 1
  const scale = Math.min((SVG_W - 2 * PAD) / w, (SVG_H - 2 * PAD) / h)
  const ox = (SVG_W - w * scale) / 2 - minX * scale
  const oy = (SVG_H - h * scale) / 2 - minY * scale
  // Flip Y for SVG coordinates so anatomical "up" stays up.
  const d = pts
    .map(([x, y], i) => {
      const sx = x * scale + ox
      const sy = SVG_H - (y * scale + oy)
      return `${i === 0 ? 'M' : 'L'}${sx.toFixed(1)},${sy.toFixed(1)}`
    })
    .join(' ') + ' Z'
  return { d, bbox: { minX, minY, maxX, maxY, w, h }, scale, ox, oy }
}

export const TemplateVisualizerDialog: React.FC<TemplateVisualizerDialogProps> = ({
  isOpen,
  onClose,
  overlay,
  onOverlayChange,
}) => {
  const [templates, setTemplates] = useState<TemplateItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(overlay?.id ?? null)
  const [error, setError] = useState<string | null>(null)
  const [filterType, setFilterType] = useState<string>('all')

  useEffect(() => {
    if (!isOpen) return
    setIsLoading(true)
    setError(null)
    backendService
      .listTemplates(1, 100)
      .then(res => {
        setTemplates(res.templates as TemplateItem[])
        setIsLoading(false)
      })
      .catch(err => {
        logError(`Failed to load templates: ${err}`, 'TemplateVisualizerDialog')
        setError(String(err))
        setTemplates([])
        setIsLoading(false)
      })
  }, [isOpen])

  useEffect(() => {
    setSelectedId(overlay?.id ?? null)
  }, [overlay?.id])

  const filtered = useMemo(() => {
    if (filterType === 'all') return templates
    return templates.filter(t => (t.type || '').toLowerCase() === filterType)
  }, [templates, filterType])

  const selected = useMemo(
    () => templates.find(t => t.id === selectedId) || null,
    [templates, selectedId],
  )

  const path = useMemo(() => {
    if (!selected?.outline_points) return null
    return buildPathFromPoints(selected.outline_points)
  }, [selected])

  const landmarkDots = useMemo(() => {
    if (!selected?.landmarks || !path) return []
    const { scale, ox, oy } = path
    return Object.entries(selected.landmarks).map(([name, coord]) => {
      const x = coord[0] * scale + ox
      const y = SVG_H - (coord[1] * scale + oy)
      return { name, x, y }
    })
  }, [selected, path])

  const isShown = overlay?.visible && overlay?.id === selected?.id

  const handleShowIn3D = () => {
    if (!selected?.outline_points) return
    onOverlayChange({
      id: selected.id,
      name: selected.name,
      points: selected.outline_points,
      landmarks: selected.landmarks,
      visible: true,
      color: '#22c55e',
    })
  }

  const handleHide = () => {
    if (overlay) {
      onOverlayChange({ ...overlay, visible: false })
    }
  }

  const handleClear = () => {
    onOverlayChange(null)
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ width: 880, maxWidth: '95vw', maxHeight: '90vh' }}
      >
        <div className="modal-header">
          <h3>Visualize Insole Template</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', gap: 16 }}>
          {/* Left: list */}
          <div style={{ width: 320, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontSize: 12 }}>Type:</label>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={{ flex: 1, padding: 4 }}
              >
                <option value="all">All</option>
                <option value="full_length">Full length</option>
                <option value="three_quarter">3/4 length</option>
                <option value="metatarsal">Metatarsal</option>
                <option value="heel_cup">Heel cup</option>
              </select>
            </div>

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: 4,
                background: '#fafafa',
                minHeight: 360,
                maxHeight: 480,
              }}
            >
              {isLoading && <div style={{ padding: 12 }}>Loading templates…</div>}
              {error && <div style={{ padding: 12, color: '#d32f2f' }}>{error}</div>}
              {!isLoading && filtered.length === 0 && (
                <div style={{ padding: 12, color: '#888' }}>No templates available.</div>
              )}
              {filtered.map(t => (
                <div
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  style={{
                    padding: '8px 10px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    background: t.id === selectedId ? '#e3f2fd' : 'transparent',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: '#666' }}>
                    {t.size || '—'} · {t.type || '—'} ·{' '}
                    {t.is_shared ? 'shared' : 'private'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: preview */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{
                border: '1px solid #ddd',
                borderRadius: 4,
                background: '#ffffff',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 8,
                minHeight: SVG_H,
              }}
            >
              {!selected && (
                <div style={{ color: '#888' }}>Select a template to preview.</div>
              )}
              {selected && !path && (
                <div style={{ color: '#d32f2f' }}>
                  Template has no outline_points (cannot render).
                </div>
              )}
              {selected && path && (
                <svg width={SVG_W} height={SVG_H} style={{ background: '#fff' }}>
                  <rect
                    width={SVG_W}
                    height={SVG_H}
                    fill="none"
                    stroke="#eee"
                    strokeWidth={1}
                  />
                  <path
                    d={path.d}
                    fill="rgba(34,197,94,0.15)"
                    stroke="#22c55e"
                    strokeWidth={2}
                  />
                  {landmarkDots.map(d => (
                    <g key={d.name}>
                      <circle cx={d.x} cy={d.y} r={4} fill="#ef4444" />
                      <text
                        x={d.x + 6}
                        y={d.y - 6}
                        fontSize={10}
                        fill="#333"
                      >
                        {d.name}
                      </text>
                    </g>
                  ))}
                </svg>
              )}
            </div>

            {selected && path && (
              <div style={{ fontSize: 12, color: '#555' }}>
                Bounding box: {path.bbox.w.toFixed(1)} mm ×{' '}
                {path.bbox.h.toFixed(1)} mm · {selected.outline_points!.length} points ·{' '}
                {Object.keys(selected.landmarks || {}).length} landmarks
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ gap: 8 }}>
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
          {overlay && (
            <button className="btn-cancel" onClick={handleClear}>
              Remove from 3D
            </button>
          )}
          {isShown ? (
            <button className="btn-cancel" onClick={handleHide}>
              Hide in 3D
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={handleShowIn3D}
              disabled={!selected?.outline_points}
            >
              Show in 3D View
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
